import React, { useMemo } from 'react';
import { Box, useMediaQuery } from '@podiumhq/design-system';
import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import { getPostalCodeErrorMessage } from 'lib/fieldValidation';
import { useKeyboardAutocompleteResults } from '../GoogleAutocomplete';
import { BusinessDetailField } from './BusinessDetailsField';
import {
  CANADIAN_COUNTRY_CODE,
  CANADIAN_PROVINCES,
  COUNTRY_OPTIONS,
  US_STATES,
} from './constants';
import GoogleAddressSearchResults from './GoogleAddressSearchResults';
import GooglePlaceSearchResults from './GooglePlaceSearchResults';
import { getBusinessDetailsDoubleFieldWrapperStyles } from './styles';

export const BusinessDetailsForm = ({
  displayManualLink,
  idPrefix = 'Trial',
  isSubmitting,
  resultsLimit,
  showFullForm = true,
  trackingEventType,
  trackingEventNamespace,
  size = 'md',
  ...props
}) => {
  const {
    businessInfo = {},
    setShowManualForm,
    showManualForm,
  } = useBusinessInfoContext() || {};
  const [isSmallerThan850px] = useMediaQuery('(max-width: 850px)');

  const {
    iconProps = {},
    enterManuallyButtonProps,
    enterManuallyText,
    noResultsTitle,
    ...resultProps
  } = props;
  const searchIconProps = {
    iconProps: iconProps,
  };

  const openManualForm = () => {
    if (!!businessInfo?.name && !showManualForm) {
      setShowManualForm(true);
    }
  };

  const {
    inputRef: inputNameRef,
    listRef: nameListRef,
    getOnKeyDown: getOnNameKeyDown,
    onKeyDown: onNameKeyDown,
    setShouldHideResults: setShouldHideNameResults,
    shouldHideResults: shouldHideNameResults,
  } = useKeyboardAutocompleteResults(openManualForm);

  const {
    inputRef: inputAddressRef,
    listRef: addressListRef,
    getOnKeyDown: getOnAddressKeyDown,
    onKeyDown: onAddressKeyDown,
    setShouldHideResults: setShouldHideAddressResults,
    shouldHideResults: shouldHideAddressResults,
  } = useKeyboardAutocompleteResults();

  const zipCodeValidation = (value) =>
    getPostalCodeErrorMessage(value, businessInfo.country);

  const fieldProps = { trackingEventType };
  const inputProps = useMemo(
    () => ({ disabled: isSubmitting, size }),
    [isSubmitting, size]
  );

  return (
    <>
      <BusinessDetailField
        autoComplete="off"
        businessFieldProps={{
          ...fieldProps,
          customOnBlur: openManualForm,
          customUserProperty: 'BusinessName',
        }}
        fieldName="name"
        id={`${idPrefix}-business-name-input`}
        inputRef={inputNameRef}
        onKeyDown={onNameKeyDown}
        placeholder="Business Name"
        {...inputProps}
        {...searchIconProps}
      />

      <GooglePlaceSearchResults
        displayManualLink={displayManualLink}
        enterManuallyButtonProps={enterManuallyButtonProps}
        enterManuallyText={enterManuallyText}
        getOnKeyDown={getOnNameKeyDown}
        listRef={nameListRef}
        noResultsTitle={noResultsTitle}
        resultsLimit={resultsLimit}
        setShouldHideResults={setShouldHideNameResults}
        shouldHideResults={shouldHideNameResults}
        trackingEventType={trackingEventType}
        trackingEventNamespace={trackingEventNamespace}
        {...resultProps}
      />

      {showFullForm && showManualForm && (
        <>
          <BusinessDetailField
            autoComplete="off"
            businessFieldProps={fieldProps}
            id={`${idPrefix}-business-address-input`}
            fieldName="address"
            inputRef={inputAddressRef}
            placeholder="Business Address"
            onKeyDown={onAddressKeyDown}
            {...inputProps}
          />
          <GoogleAddressSearchResults
            listRef={addressListRef}
            getOnKeyDown={getOnAddressKeyDown}
            setShouldHideResults={setShouldHideAddressResults}
            shouldHideResults={shouldHideAddressResults}
            {...resultProps}
          />

          <BusinessDetailField
            businessFieldProps={fieldProps}
            fieldName="addressOptional"
            id={`${idPrefix}-business-addressOptional-input`}
            placeholder="Suite/Unit/Building (optional)"
            {...inputProps}
          />

          <Box
            {...getBusinessDetailsDoubleFieldWrapperStyles({
              childOrder: 'last',
              isMobile: isSmallerThan850px,
            })}
          >
            <BusinessDetailField
              businessFieldProps={fieldProps}
              fieldName="city"
              id={`${idPrefix}-business-city-input`}
              placeholder="City"
              {...inputProps}
            />
            <BusinessDetailField
              businessFieldProps={fieldProps}
              fieldName="state"
              id={`${idPrefix}-business-state-input`}
              data-e2e={`${idPrefix}-business-state-input`}
              placeholder="State/Province"
              selectList={
                businessInfo?.country === CANADIAN_COUNTRY_CODE
                  ? CANADIAN_PROVINCES
                  : US_STATES
              }
              {...inputProps}
            />
          </Box>

          <Box
            {...getBusinessDetailsDoubleFieldWrapperStyles({
              isMobile: isSmallerThan850px,
            })}
          >
            <BusinessDetailField
              businessFieldProps={{
                ...fieldProps,
                customFieldValidation: zipCodeValidation,
              }}
              fieldName="zip"
              id={`${idPrefix}-business-zip-input`}
              placeholder="ZIP/Postal Code"
              {...inputProps}
            />
            <BusinessDetailField
              fieldName="country"
              id={`${idPrefix}-business-country-input`}
              placeholder="Country"
              selectList={COUNTRY_OPTIONS}
              {...inputProps}
            />
          </Box>
        </>
      )}
    </>
  );
};
